import React, { useState, useEffect, useRef } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  useMapEvents,
} from "react-leaflet";
import L, { LatLngBoundsExpression } from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPoint } from "../../../interface/map";
import { GeoService } from "../../../services/geo.service";

// Marker ikonları üçün tənzimləmə
const defaultIcon = L.icon({
  iconUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png",
  iconRetinaUrl:
    "https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

// Props interfeysi
interface MapProps {
  point: MapPoint | null;
  setPoint: (point: MapPoint | null) => void;
  isAddressSelected: boolean;
  setIsAddressSelected: (isAddressSelected: boolean) => void;
  address: string;
  isMarkerMoved: boolean;
  setIsMarkerMoved: (isMarkerMoved: boolean) => void;
  handlePostOnChange: (
    lat?: number,
    lng?: number,
    city?: string,
    address?: string,
  ) => Promise<void>;
  setAddress: (newAddress: string) => void;
  setCoordinates: (coords: { lat: number; lon: number; city: string }) => void;
  initialCoordinates: { lat: number; lon: number } | null;
}

const bakuBounds: LatLngBoundsExpression = [
  [40.3, 49.7],
  [40.55, 50.1],
];

const MapComponent: React.FC<MapProps> = ({
  setAddress,
  address,
  setPoint,
  isMarkerMoved,
  setIsMarkerMoved,
  handlePostOnChange,
  setIsAddressSelected,
  setCoordinates,
  initialCoordinates,
}) => {
  const [markerPosition, setMarkerPosition] = useState<L.LatLng | null>(
    initialCoordinates
      ? L.latLng(initialCoordinates.lat, initialCoordinates.lon)
      : null,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mapRef = useRef<L.Map>(null);

  const reverseGeocodeCustom = async (
    lat: number,
    lon: number,
  ): Promise<{ address: string; city: string }> => {
    try {
      const res = await GeoService.reverseGeocode(lat, lon);
      const item = (res.data?.data ?? [])[0];
      return {
        address: item?.candidateAZ || item?.candidate || "",
        city: item?.cityAZ || item?.city || "",
      };
    } catch {
      return { address: "", city: "" };
    }
  };

  // Yer detallarını almaq üçün funksiya
  const fetchLocationDetails = async (
    lat: number,
    lon: number,
  ): Promise<{ address: string; city: string }> => {
    return reverseGeocodeCustom(lat, lon);
  };

  // Yeri yeniləmək üçün funksiya
  const updateLocation = async (lat: number, lon: number) => {
    const newAddress = await fetchLocationDetails(lat, lon);
    const newPosition = L.latLng(lat, lon);

    setMarkerPosition(newPosition);
    setPoint({
      city: newAddress.city,
      lat,
      lng: lon,
    });

    setIsAddressSelected(false);
    setIsMarkerMoved(true);
    await handlePostOnChange(lat, lon, newAddress.city, newAddress.address);
    // setCoordinates({ lat, lon, city: newAddress.city });
    setIsMarkerMoved(false);
    // setAddress(newAddress.address);

    // Xəritəni yeni mövqeyə apar
    if (mapRef.current) {
      mapRef.current.flyTo(newPosition, 15);
    }
  };

  // Xəritəyə klikləmə hadisəsi
  const MapClickHandler = () => {
    useMapEvents({
      click: async (e) => {
        const { lat, lng } = e.latlng;
        await updateLocation(lat, lng);
        const data = await reverseGeocode(lat, lng);
        const addr = data.display_name || "";
        setAddress(addr);
      },
    });
    return null;
  };

  useEffect(() => {
    const fetchCoords = async () => {
      if (!initialCoordinates) {
        return;
      }
      const { lat, lon } = initialCoordinates;
      await updateLocation(lat, lon);
    };

    const timer = setTimeout(fetchCoords, 1000);
    return () => clearTimeout(timer);
  }, [initialCoordinates]);

  async function reverseGeocode(lat: number, lon: number): Promise<{ display_name?: string }> {
    const result = await reverseGeocodeCustom(lat, lon);
    return { display_name: result.address };
  }

  return (
    <div style={{ position: "relative", height: "300px", marginTop: "30px" }}>
      {isLoading && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255,255,255,0.7)",
            zIndex: 1000,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          Yüklənir...
        </div>
      )}

      {error && (
        <div
          style={{
            position: "absolute",
            top: "10px",
            left: "50%",
            transform: "translateX(-50%)",
            backgroundColor: "#ffebee",
            color: "#c62828",
            padding: "8px 16px",
            borderRadius: "4px",
            zIndex: 1000,
            boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
          }}
        >
          {error}
        </div>
      )}

      <MapContainer
        center={markerPosition || [40.4093, 49.8671]} // Baku center
        zoom={13}
        ref={mapRef}
        maxBounds={bakuBounds}
        maxBoundsViscosity={1.0}
        minZoom={12}
        maxZoom={19}
        worldCopyJump={false}
        zoomControl={true}
        scrollWheelZoom={true}
        dragging={true}
        style={{ height: "100%", width: "100%" }}
      >
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        <MapClickHandler />

        {markerPosition && (
          <Marker
            position={markerPosition}
            eventHandlers={{
              dragend: async (e) => {
                const m = e.target as L.Marker;
                const { lat, lng } = m.getLatLng();

                // 1) update coords immediately
                await updateLocation(lat, lng);

                // 2) reverse geocode to get human-readable address
                try {
                  const data = await reverseGeocode(lat, lng);
                  const addr = data.display_name || "";
                  setAddress(addr);
                } catch (err) {
                  // optionally show a toast
                  setAddress(""); // or keep previous
                }
              },
            }}
            icon={defaultIcon}
            draggable={true}
          >
            <Popup>{address || "Yer seçildi"}</Popup>
          </Marker>
        )}
      </MapContainer>
    </div>
  );
};

export default MapComponent;
